"use client";

import { useState } from "react";
import {
  User,
  FolderKanban,
  Database,
  ChartLine,
  Check,
  File,
} from "lucide-react";

type FeatureItem = {
  id: number;
  title: string;
  icon: any;
  image: string;
  features: string[];
};

const featuresData: FeatureItem[] = [
  {
    id: 1,
    title: "مدیریت بنگاه",
    icon: User,
    image: "/images/business-management.jpg",
    features: [
      "مدیریت و ساختاردهی حرفه‌ای به تمام شعب و محدوده‌های کاری",
      "تعریف نقش‌ها و سطوح دسترسی مانند مدیر شعبه، مدیر محدوده، مسئول فایل، اپراتور، مشاور فایل و حتی سرمایه‌گذار",
      "دریافت خودکار آگهی‌های مشتریان در وب‌سایت، مطابق با مناطق تحت پوشش انتخاب‌شده توسط مدیر بنگاه",
      "نمایش بنگاه در وبسایت و حضور آنلاین و معرفی معتبر بنگاه در بستر رسمی سامانه مشاور",
    ],
  },
  {
    id: 2,
    title: "مدیریت فایل ها",
    icon: FolderKanban,
    image: "/images/file-management.jpg",
    features: [
      "سیستم جامع مدیریت فایل‌ها و مستندات",
      "دسته‌بندی هوشمند فایل‌ها بر اساس نوع و اهمیت",
      "جستجوی پیشرفته در میان فایل‌ها",
      "اشتراک‌گذاری امن فایل‌ها با تیم",
    ],
  },
  {
    id: 3,
    title: "دریافت فایل از سامانه های خارجی",
    icon: Database,
    image: "/images/external-systems.jpg",
    features: [
      "اتصال خودکار به سامانه‌های خارجی",
      "دریافت خودکار فایل‌ها و اطلاعات",
      "همگام‌سازی لحظه‌ای داده‌ها",
      "پشتیبانی از فرمت‌های مختلف فایل",
    ],
  },
  {
    id: 4,
    title: "گزارش گیری",
    icon: ChartLine,
    image: "/images/reporting.jpg",
    features: [
      "گزارش‌گیری جامع و لحظه‌ای",
      "داشبوردهای مدیریتی قابل تنظیم",
      "خروجی‌های مختلف (PDF، Excel، Word)",
      "تحلیل و آنالیز داده‌های پیشرفته",
    ],
  },
  {
    id: 5,
    title: "دریافت آگهی از مشتریان سامانه",
    icon: File,
    image: "/images/customer-ads.jpg",
    features: [
      "دریافت خودکار آگهی‌های مشتریان",
      "فیلترسازی هوشمند آگهی‌ها",
      "اعلان‌های لحظه‌ای برای آگهی‌های جدید",
      "مدیریت و پیگیری آگهی‌ها",
    ],
  },
];

function FeatureCard({
  feature,
  isActive,
  onClick,
}: Readonly<{
  feature: FeatureItem;
  isActive: boolean;
  onClick: () => void;
}>) {
  const Icon = feature.icon;

  return (
    <button
      type="button"
      className={`
          group flex items-center justify-center
          rounded-xl transition-all duration-300 cursor-pointer
          ${isActive
          ? "bg-primary text-white border-2 border-primary "
          : "bg-[#F9FAFB] hover:bg-gray-50 text-gray-700 hover:border-primary/30"
        }
          px-3 sm:px-4 py-2 sm:py-3 mx-1
          w-full max-w-full sm:max-w-56 lg:max-w-72.5
          hover:scale-[1.02] active:scale-[0.98]
          min-h-17.5 sm:min-h-20
        `}
      onClick={onClick}
    >
      <div className="flex flex-col sm:flex-row gap-1 sm:gap-2 items-center text-center w-full">
        {/* Icon Container */}
        <div
          className={`
              flex items-center justify-center
              w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full transition-all duration-300
            `}
        >
          <Icon
            size={24}
            className={`${isActive ? "text-white" : "text-primary"}`}
          />
        </div>

        {/* Title */}
        <h3
          className={`
              text-xs sm:text-sm font-medium transition-colors
              ${isActive ? "text-white font-semibold" : "text-gray-800"}
              line-clamp-2 flex-1
            `}
        >
          {feature.title}
        </h3>
      </div>
    </button>
  );
}

function FeatureDetail({
  feature,
}: Readonly<{
  feature: FeatureItem;
}>) {
  return (
    <div className="mt-6 sm:mt-8 lg:mt-10 animate-fadeIn">
      <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-6 lg:gap-8">
        {/* Right side - Features list */}
        <div className="w-full lg:w-2/5 order-2 lg:order-1">
          <div className="space-y-3 sm:space-y-4">
            {feature.features.map((item) => (
              <div
                key={item}
                className="flex items-start gap-3 bg-white p-3 sm:p-4 rounded-xl   border-gray-100 hover:border-primary/20 transition-all duration-300"
              >
                {/* Check icon on right side (RTL) */}
                <div className="shrink-0">
                  <div
                    className="
                      w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 rounded-full
                      bg-[#12B76A] text-white
                      flex items-center justify-center
                    "
                  >
                    <Check size={12} />
                  </div>
                </div>

                {/* Text content */}
                <div className="flex-1 text-right">
                  <p className="text-gray-800 text-xs sm:text-sm leading-relaxed lg:leading-loose">
                    {item}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Left side - Image */}
        <div className="w-full lg:w-3/5 order-1 lg:order-2">
          <div className="relative rounded-2xl overflow-hidden ">
            <div className="flex items-center justify-center">
              <img
                src={"/assets/frame.png"}
                alt={feature.title}
                className="w-full h-auto max-h-62.5 sm:max-h-75 lg:max-h-100 object-cover rounded-xl"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const FeaturesSection = () => {
  const [activeCard, setActiveCard] = useState<number | null>(1);

  return (
    <section className="w-full flex flex-col items-center px-3 sm:px-4 lg:px-6 mt-12 sm:mt-16 lg:mt-18 max-w-7xl xl:max-w-9xl mx-auto">
      <h2 className="text-black text-2xl sm:text-3xl lg:text-4xl font-bold p-2 rounded mt-2 text-center">
        امکانات ویژه{" "}
        <span className="text-primary">
          مشاور<span className="text-black"> برای مشاورین املاك </span>
        </span>
      </h2>

      <div className="flex items-center mt-4 sm:mt-6 lg:mt-8 text-gray-500 text-sm sm:text-base max-w-2xl text-center px-2">
        <span className="font-medium">
          ویژگی‌های برجسته زیر، مشاور را از دیگر CRM ها متمایز می‌کند...
        </span>
      </div>

      {/* Cards Row Container */}
      <div className="w-full mt-8 sm:mt-10 lg:mt-12">
        {/* Mobile: Vertical stack of cards */}
        <div className="block sm:hidden">
          <div className="flex flex-col gap-2">
            {featuresData.map((feature) => (
              <FeatureCard
                key={feature.id}
                feature={feature}
                isActive={activeCard === feature.id}
                onClick={() => setActiveCard(feature.id)}
              />
            ))}
          </div>
        </div>

        {/* Tablet & Desktop: Horizontal scrollable cards */}
        <div className="hidden sm:block">
          <div className="flex justify-center items-center space-x-1 overflow-x-auto pb-4 px-2 scrollbar-hide">
            {featuresData.map((feature) => (
              <FeatureCard
                key={feature.id}
                feature={feature}
                isActive={activeCard === feature.id}
                onClick={() => setActiveCard(feature.id)}
              />
            ))}
          </div>
        </div>

        {/* Active Feature Detail */}
        {activeCard ? (
          <FeatureDetail
            feature={featuresData.find((f) => f.id === activeCard)!}
          />
        ) : (
          /* Default message when no card is selected */
          <div className="mt-8 sm:mt-10 lg:mt-12 flex flex-col items-center text-center py-8 sm:py-10 lg:py-12">
            <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-full bg-gray-100 flex items-center justify-center mb-4 sm:mb-6">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
            <div className="text-gray-400 text-lg sm:text-xl font-medium mb-2">
              روی یکی از امکانات کلیک کنید
            </div>
            <div className="text-gray-500 text-sm sm:text-base max-w-md px-4">
              برای مشاهده جزئیات کامل هر امکان، شامل تصویر و ویژگی‌های آن، روی
              کارت مربوطه کلیک کنید
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturesSection;
